import React, { useMemo } from 'react';
import { parseUPSCQuestion, tidy, stripEmbeddedOptions } from '../../services/upscQuestion';
import ImportantTermText from '../ImportantTermText/ImportantTermText';
import './UPSCQuestion.css';

// Renders a question the way UPSC prints it — statements, lists, match-the-following
// tables — using the SAME parser as the PDF export, so app and paper always agree.
// Options are NOT rendered here; keep rendering them from card.options as before.

const SHORT_ITEM = 28; // chars; short list items sit two per row (like "I. Bonds   II. Hedge Funds")

function Table({ table }) {
  const hasLabel = table.rows.some((r) => r.label);
  return (
    <div className="uq-table-wrap">
      <table className="uq-table">
        {table.header && (
          <thead>
            <tr>
              {hasLabel && <th aria-hidden="true" />}
              {table.header.map((h, i) => <th key={i}><ImportantTermText text={h} /></th>)}
            </tr>
          </thead>
        )}
        <tbody>
          {table.rows.map((r, i) => (
            <tr key={i}>
              {hasLabel && <td className="uq-rowlabel">{r.label}</td>}
              {r.cells.map((c, j) => <td key={j}><ImportantTermText text={c} /></td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function UPSCQuestion({ text }) {
  const q = useMemo(() => parseUPSCQuestion(text), [text]);

  // plain questions keep their own paragraph breaks
  if (q.kind === 'simple') {
    const lines = stripEmbeddedOptions(tidy(text)).split('\n').map((l) => l.trim()).filter(Boolean);
    return (
      <div className="uq">
        {lines.map((l, i) => <p key={i} className="uq-stem"><ImportantTermText text={l} /></p>)}
      </div>
    );
  }

  const stem = q.stem || (q.kind === 'statements' ? 'Consider the following statements:' : '');
  const grid = q.kind === 'items' && q.items.length >= 2 && q.items.every((it) => it.text.length <= SHORT_ITEM);

  return (
    <div className="uq">
      {stem && <p className="uq-stem"><ImportantTermText text={stem} /></p>}

      {q.kind === 'items' && (
        <div className={`uq-items${grid ? ' uq-items--grid' : ''}`}>
          {q.items.map((it, i) => (
            <div key={i} className="uq-item">
              <span className="uq-label">{it.label}</span>
              <span className="uq-text"><ImportantTermText text={it.text} /></span>
            </div>
          ))}
        </div>
      )}

      {q.kind === 'statements' && q.blocks.map((b, i) => (
        <div key={i} className="uq-block">
          <div className="uq-block-label">{b.label}</div>
          <p className="uq-block-text"><ImportantTermText text={b.text} /></p>
        </div>
      ))}

      {q.kind === 'table' && <Table table={q.table} />}

      {q.tail.map((t, i) => <p key={i} className="uq-tail"><ImportantTermText text={t} /></p>)}
    </div>
  );
}